/**
 * Em andamento: uma linha por execução aberta (cada lote no seu passo).
 * "Continuar" abre exatamente no passo onde parou. Paradas há mais de 1 dia
 * útil sobem para o topo com a faixa âmbar suave — sem vermelho, sem alarme
 * (decisão do design). Lista com divisórias, um sinal de cor por linha.
 *
 * Lê do pacote do aparelho, então funciona sem internet; os toques ainda na
 * fila já aparecem (projeção local).
 */
import React, { useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { EmptyState } from "../../components/EmptyState";
import { PageLoader } from "../../components/LoadingMarcenaria";
import Casca, { BASE } from "./Casca";
import { ROTA_EXECUCAO } from "./componentes/IniciarExecucao";
import { estadoLocal, posicao } from "./dominio/execucao";
import { ROTULO_STATUS, TOM_STATUS, haQuantoTempo, percentual } from "./dominio/formatos";
import { STATUS } from "./dominio/projecao";
import { useAreaPcp } from "./offline/AreaPcpContext";
import {
  BotaoPrimario,
  BotaoSecundario,
  Corpo,
  Linha,
  Lista,
  Meta,
  Mono,
  Progresso,
  Segmentado,
  Selo,
  Sub,
  TituloLinha,
} from "./styles";

const FILTROS = [
  { id: "todas", rotulo: "Todas" },
  { id: STATUS.EM_ANDAMENTO, rotulo: "Ativas" },
  { id: STATUS.PAUSADA, rotulo: "Pausadas" },
];

/** A execução como a tela mostra: resumo do servidor + toques ainda na fila. */
export function resumoLocal(execucao, pendentes) {
  const { unidades, estado } = estadoLocal(execucao, pendentes);
  const onde = posicao(execucao.roteiro, unidades, estado);
  return {
    ...execucao,
    status: estado.status,
    motivo_pausa: estado.motivo_pausa,
    numero: onde.numero,
    titulo_passo: onde.passo?.titulo_curto || "",
    pct: percentual(estado.concluidos, estado.total),
    temPendente: Boolean(pendentes?.length),
  };
}

function ordenar(itens) {
  return [...itens].sort((a, b) => {
    if (a.parada !== b.parada) return a.parada ? -1 : 1;
    return String(b.ultima_atividade_em).localeCompare(String(a.ultima_atividade_em));
  });
}

function LinhaExecucao({ item, onContinuar }) {
  return (
    <Linha $tom={item.parada ? "warning" : undefined}>
      <Corpo>
        <TituloLinha>
          <Mono>{item.identificador || item.tarefa.titulo}</Mono>
        </TituloLinha>
        <Sub>
          {item.identificador ? `${item.tarefa.titulo} · ` : ""}Passo {item.numero} de {item.total_passos}
          {item.titulo_passo ? ` · ${item.titulo_passo}` : ""}
        </Sub>
        <Progresso $pct={item.pct} $altura={4} aria-label={`${item.pct}% concluído`}>
          <span />
        </Progresso>
        <Meta>
          <Selo $tom={TOM_STATUS[item.status]}>{ROTULO_STATUS[item.status]}</Selo>
          {item.motivo_pausa ? <span>{item.motivo_pausa}</span> : null}
          {item.parada ? <span>Parado há {item.dias_uteis_parada} dias úteis</span> : null}
          <span>
            {item.ultima_atividade_por || item.iniciada_por || "—"} · {haQuantoTempo(item.ultima_atividade_em)}
          </span>
          {item.temPendente ? <span>alterações a enviar</span> : null}
        </Meta>
      </Corpo>
      <BotaoPrimario type="button" onClick={() => onContinuar(item.id)} aria-label={`Continuar ${item.identificador || item.tarefa.titulo}`}>
        Continuar
      </BotaoPrimario>
    </Linha>
  );
}

export default function EmAndamento() {
  const { pacote, pendentes } = useAreaPcp();
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState("todas");

  const itens = useMemo(() => {
    const abertas = (pacote?.execucoes || [])
      .map((e) => resumoLocal(e, pendentes[e.id]))
      .filter((e) => e.status === STATUS.EM_ANDAMENTO || e.status === STATUS.PAUSADA);
    return ordenar(abertas);
  }, [pacote, pendentes]);

  const visiveis = filtro === "todas" ? itens : itens.filter((i) => i.status === filtro);
  const contagem = (id) => (id === "todas" ? itens.length : itens.filter((i) => i.status === id).length);

  if (!pacote) return <PageLoader scope="kpis" />;

  return (
    <Casca
      contagemAbertas={itens.length}
      acoes={
        <BotaoSecundario type="button" aria-label="Nova execução" onClick={() => navigate(`${BASE}/tarefas`)}>
          <FiPlus size={14} aria-hidden="true" />
          <span className="so-desktop">Nova execução</span>
        </BotaoSecundario>
      }
    >
      {itens.length ? (
        <>
          <Segmentado role="group" aria-label="Filtrar execuções" style={{ alignSelf: "flex-start" }}>
            {FILTROS.map((f) => (
              <button key={f.id} type="button" aria-pressed={filtro === f.id} onClick={() => setFiltro(f.id)}>
                {f.rotulo} · {contagem(f.id)}
              </button>
            ))}
          </Segmentado>
          <Lista aria-label="Execuções abertas">
            {visiveis.map((item) => (
              <LinhaExecucao key={item.id} item={item} onContinuar={(id) => navigate(ROTA_EXECUCAO(id))} />
            ))}
          </Lista>
        </>
      ) : (
        <EmptyState
          tone="ok"
          titulo="Nenhuma execução aberta"
          descricao="Quando um lote começar, ele aparece aqui com o passo em que parou."
          acoes={[{ label: "Ver todas as tarefas", onClick: () => navigate(`${BASE}/tarefas`) }]}
        />
      )}
    </Casca>
  );
}
